import {useCallback, useMemo, useRef, useState} from 'react';
import assert from 'assert-ts';
import type {AgentSubType} from 'types';
import type {CodeListId, Schema} from 'schemaDefinition/types';
import type {Agent} from 'services/agent/types';
import {useLocalization} from 'localization';
import type {LinkedRoleCodeListId} from 'schemaDefinition/types/linkTypes';
import {areAgentsEqual} from 'services/agent';
import {useCodelists} from 'services/codelists';
import {getErrorMessage} from 'services/utils/getErrorMessage';
import {useSnacks} from 'components';
import {validate} from 'schema/form/functions/validators/validate';
import {useSchemaCodelistIds} from 'schema/hooks';
import {useAgentSchema} from 'schemas/hooks';
import type {EditorMode} from '../../types';
import type {EditAgentProps} from '../types';
import {createEmptyAgent, getSavedAgentMessage} from '../functions';

type EditAgentState = {
  agentType: AgentSubType;
  schema: Schema;
  codelistIds: (CodeListId | LinkedRoleCodeListId)[];
  mode: EditorMode;
  // Initial value of agent when going to edit mode
  initialEditAgent: Agent;
  hasChanges: boolean;
  showErrors: boolean;
  onChanged: (agent: Agent) => void;
  // Consider support for confirm save with warnings
  save: () => Promise<void>;
};

export const useEditAgentState = ({
  agentType,
  suggestion,
  originalAgent,
  onSave,
}: EditAgentProps): EditAgentState => {
  const mode: EditorMode = originalAgent ? 'edit' : 'register';
  const resolvedType = assert(
    agentType ?? originalAgent?.agentType,
    'agentType is required',
  );
  const localization = useLocalization();
  const {t} = localization;
  const schema = useAgentSchema(resolvedType);
  const codelistIds = useSchemaCodelistIds(schema);
  const codelistMap = useCodelists(codelistIds);

  const [initialEditAgent, setInitialEditAgent] = useState(
    originalAgent ?? createEmptyAgent(resolvedType, suggestion),
  );

  const editedAgentRef = useRef<Agent>(initialEditAgent);
  const [hasChanged, setHasChanged] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const {successSnack, errorSnack, warningSnack} = useSnacks();

  const handleChanged = useCallback(
    (agent: Agent) => {
      editedAgentRef.current = agent;
      setHasChanged(!areAgentsEqual(initialEditAgent, editedAgentRef.current));
    },
    [initialEditAgent],
  );

  const handleSave = useCallback((): Promise<void> => {
    const validation = validate(
      editedAgentRef.current,
      undefined,
      schema,
      assert(codelistMap, 'useEditAgentWizard: codelistMap is required'),
    );

    if (validation.valid !== 'valid') {
      setShowErrors(true);
      if (validation.valid === 'warning') {
        warningSnack(validation.errors.map(e => e.value).join(', '));
      } else {
        if (validation.errors.filter(e => e.value).length > 0) {
          errorSnack(validation.errors.map(e => e.value).join(', '));
        }
        return Promise.resolve();
      }
    }

    return onSave(assert(editedAgentRef.current), 'bbOnly')
      .then(savedAgent => {
        const msg = getSavedAgentMessage(mode, t);
        successSnack(msg);
        setInitialEditAgent(savedAgent);
      })
      .catch(error => {
        const message = getErrorMessage(
          error,
          'page.agents.save.failed',
          localization,
        );
        errorSnack(message);
      });
  }, [
    schema,
    codelistMap,
    onSave,
    warningSnack,
    errorSnack,
    mode,
    t,
    successSnack,
    localization,
  ]);

  return useMemo(() => {
    return {
      agentType: resolvedType,
      schema,
      codelistIds,
      mode,
      // Initial value of agent when going to edit mode
      initialEditAgent,
      hasChanges: mode === 'register' || hasChanged,
      showErrors,
      onChanged: handleChanged,
      save: handleSave,
    };
  }, [
    resolvedType,
    schema,
    codelistIds,
    mode,
    initialEditAgent,
    hasChanged,
    showErrors,
    handleChanged,
    handleSave,
  ]);
};
